/**
 * "Plan limits in the app" (design 10 · Website & pricing, k9qobd): the locked widgets in the library, the Brief me
 * monthly limit card and the seat limit card on Access → Invite. Model and copy: plans/planLimits.ts.
 */
import { useState } from "react";
import { ArrowRight, Lock, Plus, Users } from "lucide-react";
import type { OrgRole } from "../nav/navConfig";
import { OvIcon } from "../overview/icons";
import { List, ListRow, RowLead, RowMain, RowTitle, RowTrail } from "../components/shared/List";
import {
  briefLimitCopy, COMPARE_PLANS, CONTACT_SALES, lockTitle, seatLimitCopy,
  type BriefAllowance, type LockedWidget, type SeatInfo,
} from "./planLimits";

/** 1 · Widget library: rows for widgets outside the plan, with the plan's lock badge; selecting one explains it. */
export function LockedWidgetRows({ items }: { items: LockedWidget[] }) {
  const [open, setOpen] = useState<string | null>(null);
  if (!items.length) return null;
  const sel = items.find((w) => w.type === open) ?? null;
  return (
    <div className="plan-locked">
      <List className="plan-locked__list">
        {items.map((w) => (
          // The row's button is written out (not ListRow onOpen) so it keeps aria-expanded / aria-controls; ls-row--hit
          // and ls-hit make it cover the row like any List row.
          <ListRow key={w.type} className="plan-locked__row ls-row--hit" selected={open === w.type}>
            <RowLead className="plan-locked__icon"><OvIcon name={w.icon} size={15} /></RowLead>
            <RowMain min={80}>
              <RowTitle className="plan-locked__name">
                <button type="button" className="ls-hit" aria-expanded={open === w.type} aria-controls="plan-locked-pop" onClick={() => setOpen((o) => (o === w.type ? null : w.type))}>{w.title}</button>
              </RowTitle>
            </RowMain>
            <RowTrail><span className="plan-badge"><Lock size={11} aria-hidden />{w.plan.name}</span></RowTrail>
          </ListRow>
        ))}
      </List>
      {sel && (
        <div className="plan-pop" id="plan-locked-pop" role="note">
          <p className="plan-pop__title"><Lock size={14} aria-hidden />{lockTitle(sel)}</p>
          <p className="plan-pop__text">{sel.text}</p>
          <a className="plan-pop__link" href={COMPARE_PLANS}>Compare plans<ArrowRight size={14} aria-hidden /></a>
        </div>
      )}
    </div>
  );
}

/** 2 · Monthly limit, Brief me. Owners and admins can buy more; everyone else sees "Ask an admin". */
export function BriefLimitCard({ allowance, role, onDismiss }: { allowance: BriefAllowance & { limit: number }; role: OrgRole | null; onDismiss: () => void }) {
  const c = briefLimitCopy(allowance, role);
  return (
    <section className="plan-card plan-limit" aria-labelledby="plan-brief-title">
      <h2 className="plan-card__title" id="plan-brief-title">{c.title}</h2>
      <div className="plan-meter">
        <div className="plan-meter__line"><span>{c.meterLabel}</span><b>{c.meterValue}</b></div>
        <div className="plan-meter__track" role="meter" aria-label={c.meterLabel} aria-valuemin={0} aria-valuemax={allowance.limit} aria-valuenow={Math.min(allowance.used, allowance.limit)}>
          <span style={{ transform: `scaleX(${c.fill})` }} />
        </div>
      </div>
      <p className="plan-card__text">{c.text}</p>
      <div className="plan-card__actions">
        {c.primary && (c.canBuy
          ? <a className="plan-btn plan-btn--primary" href={CONTACT_SALES}><Plus size={14} aria-hidden />{c.primary}</a>
          : <span className="plan-btn plan-btn--primary" aria-disabled="true">{c.primary}</span>)}
        <button type="button" className="plan-btn" onClick={onDismiss}>{c.secondary}</button>
      </div>
    </section>
  );
}

/** 3 · Seat limit, Access → Invite: the invite card when every seat is in use. */
export function SeatLimitCard({ seats, orgName, email, onEmail, onManage }: {
  seats: SeatInfo & { limit: number }; orgName: string; email: string; onEmail: (v: string) => void; onManage: () => void;
}) {
  const c = seatLimitCopy(seats, orgName);
  return (
    <section className="plan-card plan-seats" aria-labelledby="plan-seat-title">
      <h2 className="plan-card__title" id="plan-seat-title">{c.title}</h2>
      <input className="plan-input" type="email" value={email} onChange={(e) => onEmail(e.target.value)} aria-label="Email" autoFocus />
      <div className="plan-banner" role="status">
        <Users size={15} aria-hidden />
        <div><b>{c.banner}</b>{c.text && <span>{c.text}</span>}</div>
      </div>
      <div className="plan-card__actions plan-card__actions--tight">
        <a className="plan-btn plan-btn--primary" href={CONTACT_SALES}><Plus size={14} aria-hidden />{c.primary}</a>
        <button type="button" className="plan-btn" onClick={onManage}>{c.secondary}</button>
      </div>
    </section>
  );
}
